import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';

import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Megaphone, Headphones, ArrowRight, CheckCircle2, MonitorSmartphone,
  MessageCircle, Search, Rocket, HeartHandshake, SearchCheck,
  Clock, Zap, ChevronDown, ShieldCheck, Lightbulb,
} from 'lucide-react';
import PageGlow from "@/components/PageGlow";
import HeroWave from "@/components/HeroWave";

const GRAD    = '#5048E5';
const fg      = 'hsl(var(--foreground))';
const fgMuted = 'hsl(var(--muted-foreground))';
const surface = 'hsl(var(--card))';
const border  = 'hsl(var(--border))';
const indigo = 'hsl(243 75% 59%)';
const coral  = 'hsl(14 100% 57%)';

const fadeUp = {
  hidden:  { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' as const } },
};
const stagger = {
  hidden:  {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const SERVICES = [
  {
    id: 'company-setup',
    Icon: Rocket,
    color: indigo,
    title: 'Vision to Launch',
    stage: 'Starting out',
    tagline: 'From idea to fully launched business',
    href: '/services/vision-to-launch',
    desc: 'Brand direction, visual identity, social media branding, SEO, AEO & GEO foundations, Google Business setup, company registration support, and a full launch checklist — everything to go from idea to live.',
    bullets: ['Brand identity & graphic design across key platforms', 'Custom digital infrastructure, fully integrated for your business requirements', 'Company registration support & launch guidance'],
  },
  {
    id: 'ai-web-app-design',
    Icon: LayoutDashboard,
    color: indigo,
    title: 'AI Web & App Design',
    stage: 'Getting online',
    tagline: 'Your digital shopfront, built fast',
    href: '/services/ai-web-app-design',
    desc: 'We build you a website or app, or improve your existing one. Using AI-assisted workflows we go from brief to live in weeks, not months — without cutting corners on quality.',
    bullets: ['New websites & apps built from scratch', 'Existing site audits & improvements', 'Mobile-first, SEO, AEO & GEO-ready, you own everything'],
  },
  {
    id: 'client-portal',
    Icon: MonitorSmartphone,
    color: indigo,
    title: 'Branded Client Portal & App',
    stage: 'Serving your clients',
    tagline: 'Your own portal, your branding',
    href: '/services/client-portal',
    desc: 'Your own branded client portal and mobile app — powered by the same system that runs Digitally Baffled. Clients log in to track progress, sign agreements, pay invoices, message you and book calls, all under your brand.',
    bullets: ['Live progress, e-signatures & card payments in one place', 'Fully rebranded — your logo, colours & domain', 'Works as an app on your clients\u2019 phones'],
  },
  {
    id: 'marketing',
    Icon: Megaphone,
    color: indigo,
    title: 'Marketing',
    stage: 'Getting found',
    tagline: 'Advertise on the platforms that matter',
    href: '/services/marketing',
    desc: 'We set up your advertising no matter the platform — PPC, Google Ads, Facebook Ads, Instagram Ads, and more. Campaigns built around real business goals, not vanity metrics.',
    bullets: ['PPC & Google Ads setup & management', 'Social media ad campaigns', 'Organic social — post writing, planning & scheduling', 'Audience targeting, retargeting & reporting'],
  },
  {
    id: 'ongoing-support',
    Icon: Headphones,
    color: indigo,
    title: 'Ongoing Support',
    stage: 'Staying on track',
    tagline: 'Your long-term digital partner, always on hand',
    href: '/services/ongoing-support',
    desc: 'Website updates, design and content changes, technical support, SEO, AEO & GEO improvements, account management, and general digital help — everything you need to keep growing after launch.',
    bullets: ['Website, design & content updates', 'SEO, AEO & GEO improvements', 'Technical support & account management'],
  },
  {
    id: 'software-audit',
    Icon: SearchCheck,
    color: coral,
    title: 'Software Audit & Consolidation',
    stage: 'Already up and running?',
    tagline: 'One week to see what you\u2019re really paying for',
    href: '/services/software-audit',
    desc: 'Most established businesses pay for tools they barely use, that don\u2019t talk to each other. In one week we map the lot and show you what to cancel, keep and replace — with the monthly saving quantified.',
    bullets: ['Full inventory of tools, subscriptions & monthly costs', 'Plain-English cancel / keep / replace Blueprint', 'Fixed £495 — most audits save more than they cost'],
  },
];

const STEPS = [
  { number: '01', Icon: MessageCircle, color: indigo, title: 'Free discovery call',  duration: '30 minutes',       description: "We start with a relaxed, no-pressure conversation. Tell us about your business, where you're stuck, and what you'd love to achieve. We listen first — no pitching, no jargon, no hard sell.", outcomes: ['We understand your goals and challenges', 'You understand how we work and what to expect', "We agree whether there's a good fit"] },
  { number: '02', Icon: Search,        color: indigo, title: 'Audit & strategy',     duration: '3–5 days',         description: "We dig into your current digital setup — website, social presence, tools, processes. We identify what's working, what's wasting your money, and where the biggest opportunities are. Then we put together a clear, prioritised plan.", outcomes: ['Full audit of your existing digital presence', 'Prioritised list of recommendations', 'Clear scope, timeline, and transparent pricing'] },
  { number: '03', Icon: LayoutDashboard, color: indigo, title: 'Your client portal',  duration: 'Yours from day one', description: "Right after we agree to work together, you get your own secure client portal — the heart of how we work. It's where you track every milestone in real time, sign service agreements, pay invoices, send voice notes and messages, and even take a call from us. Everything you do flows straight to our admin portal, so our team picks it up instantly — no chasing, no lost emails, no wondering what's happening.", outcomes: ['Live progress tracking on every milestone', 'Sign agreements & pay invoices in one place', 'Message, call & share files — synced straight to our team'] },
  { number: '04', Icon: Rocket,        color: indigo, title: 'Build & launch',       duration: 'Weeks, not months', description: "We get to work. Using AI-assisted workflows, we move fast without cutting corners. You get regular updates in plain English — no technical jargon, no radio silence. You review and approve before anything goes live.", outcomes: ['Regular progress updates throughout', 'You review and approve before launch', 'Everything set up in your name — you own it all'] },
  { number: '05', Icon: HeartHandshake, color: indigo, title: 'Ongoing support',     duration: 'As long as you need', description: "We don't disappear after launch. We're here for ongoing support, optimisation, and growth. Whether that's monthly check-ins, ad management, or just being on call when something needs fixing — we're your long-term digital partner — and your portal stays open the whole way.", outcomes: ['Ongoing monitoring and optimisation', 'Regular performance reports in plain English', 'A partner you can call when things change'] },
];



const PRINCIPLES = [
  { Icon: ShieldCheck,    color: indigo, title: 'You own everything',       body: 'Your domain, your website, your accounts. We set it all up in your name and hand you the keys. No lock-in, ever.' },
  { Icon: MessageCircle,  color: indigo, title: 'Plain English, always',    body: "We never hide behind jargon. If you don't understand what we're doing or why, we haven't done our job." },
  { Icon: Zap,            color: indigo, title: 'Fast without shortcuts',   body: 'AI-assisted workflows let us move quickly — but we never sacrifice quality. Professional results in weeks, not months.' },
  { Icon: Clock,          color: indigo, title: 'Transparent timelines',    body: "We give you realistic estimates upfront and stick to them. No nasty surprises, no scope creep without your sign-off." },
];

import { FAQ_CATEGORIES } from './services.seo';
// JSON-LD lives in ./services.seo so route head() stays lightweight.



function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="overflow-hidden rounded-xl transition-all" style={{ background: surface, border: `1px solid ${border}` }}>
      <button onClick={() => setOpen(v => !v)} className="flex w-full items-center justify-between gap-4 px-6 py-4 text-left" aria-expanded={open}>
        <span className="text-sm font-semibold leading-snug" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fg }}>{q}</span>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.25 }} className="shrink-0">
          <ChevronDown size={16} style={{ color: fgMuted }} />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div key="body" initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.28, ease: 'easeInOut' }} style={{ overflow: 'hidden' }}>
            <p className="px-6 pb-5 text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function FaqCategories() {
  const [activeCategory, setActiveCategory] = useState(0);
  return (
    <div>
      {/* Category tabs */}
      <div className="mb-6 flex flex-wrap gap-2">
        {FAQ_CATEGORIES.map(({ label }, i) => (
          <button
            key={label}
            onClick={() => setActiveCategory(i)}
            className="rounded-full px-4 py-1.5 text-xs font-semibold transition-all"
            style={{
              background: activeCategory === i ? GRAD : 'hsla(243,75%,59%,0.08)',
              color: activeCategory === i ? '#ffffff' : fgMuted,
              border: `1px solid ${activeCategory === i ? GRAD : border}`,
              fontFamily: 'Plus Jakarta Sans, sans-serif',
            }}>
            {label}
          </button>
        ))}
      </div>
      {/* FAQ items */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeCategory}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
          className="flex flex-col gap-3">
          {FAQ_CATEGORIES[activeCategory].faqs.map(({ q, a }) => (
            <FaqItem key={q} q={q} a={a} />
          ))}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default function ServicesPage() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
      }
    }
  }, [hash]);
  return (
    <>


      {/* ── Hero ── */}
      <section className="relative overflow-hidden pb-28 pt-10 md:pt-14" style={{ background: 'hsl(var(--background))' }}>
        <HeroWave fadeBottom={58} bleed="bottom" />
        <PageGlow tall />
        <div className="relative mx-auto max-w-3xl px-6 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.h1 variants={fadeUp} className="mb-5 text-4xl font-bold md:text-6xl" style={{ fontFamily: 'Outfit, sans-serif', color: fg, lineHeight: 1.05 }}>
              Everything digital.{' '}
              <span style={{ background: `linear-gradient(135deg, ${GRAD} 0%, hsl(14 100% 57%) 100%)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                Nothing baffling.
              </span>
            </motion.h1>
            <motion.p variants={fadeUp} className="mb-8 text-lg leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
              We handle the full digital picture for small businesses — from your website and Google presence to compliance, analytics, and everything in between.
            </motion.p>
            <motion.p variants={fadeUp} className="mb-8 text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fg }}>
              Wherever your business is on its journey, there's one clear place to start.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-wrap items-center justify-center gap-3">
              <Link to="/get-started" className="btn-glow inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold transition-all" style={{ background: GRAD, color: '#ffffff', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Get Started <ArrowRight size={15} />
              </Link>
              <Link to="/faq" className="btn-glass-hover inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold transition-all" style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid var(--glass-border)', boxShadow: 'var(--glass-shadow)', color: 'var(--glass-color)', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Read the FAQ
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── Services grid ── */}
      <section className="-mt-20 pb-24 pt-20" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-6xl px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map(({ id, Icon, color, title, stage, tagline, desc, bullets, href }, i) => {
              const isAlternate = i === SERVICES.length - 1;
              return (
              <motion.div key={title} variants={fadeUp} id={id} className="flex h-full flex-col rounded-2xl transition-all hover:-translate-y-1" style={{ background: isAlternate ? 'hsla(14,100%,57%,0.06)' : surface, border: `1px solid ${isAlternate ? 'hsla(14,100%,57%,0.28)' : border}` }}>

                {/* ── Service detail → service page ── */}
                <Link to={href} className="group flex flex-1 flex-col p-7">
                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl" style={{ background: `color-mix(in srgb, ${color} 12%, transparent)` }}>
                    <Icon size={22} style={{ color }} />
                  </div>
                  <div className="mb-1 text-xs font-semibold uppercase tracking-widest" style={{ color: GRAD, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{stage}</div>
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wider" style={{ color: fgMuted, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{tagline}</div>
                  <h2 className="mb-3 text-lg font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>{title}</h2>
                  <p className="mb-5 text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>{desc}</p>
                  <ul className="mt-auto flex flex-col gap-2">
                    {bullets.map(b => (
                      <li key={b} className="flex items-start gap-2 text-sm" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
                        <CheckCircle2 size={14} className="mt-0.5 shrink-0" style={{ color }} />
                        {b}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 flex items-center justify-end gap-1 pt-1">
                    <span className="flex items-center gap-1 text-xs font-semibold transition-opacity group-hover:opacity-70" style={{ color: GRAD, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                      Learn more <ArrowRight size={12} className="transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </Link>

              </motion.div>
            )})}
          </motion.div>
          <motion.p
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: 0.2 }}
            className="mt-5 text-center text-xs leading-relaxed"
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
            Guide prices only — every business is different. After getting started you'll receive a clear, fixed service agreement before any work begins.{' '}
            <span className="font-semibold" style={{ color: fg }}>No hidden fees. No surprise invoices.</span>
          </motion.p>
        </div>
      </section>

      {/* ── Not limited disclaimer ── */}
      <section className="pb-10 pt-2" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-6xl px-6">
          <motion.div className="relative overflow-hidden rounded-2xl px-8 py-7 md:px-12 md:py-8" style={{ background: `linear-gradient(135deg, color-mix(in srgb, ${GRAD} 10%, transparent) 0%, color-mix(in srgb, hsl(14 100% 57%) 8%, transparent) 100%)`, border: `1px solid color-mix(in srgb, ${GRAD} 25%, transparent)` }}>
            <div className="relative flex flex-col gap-4 md:flex-row md:items-center md:gap-8">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl" style={{ background: GRAD }}>
                <Lightbulb size={22} style={{ color: '#ffffff' }} />
              </div>
              <div className="flex-1">
                <p className="mb-1 text-base font-bold md:text-lg" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>
                  We're not limited to what's listed above.
                </p>
                <p className="text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
                  Bring us a problem and, as your personal business partner, we'll work on a solution. If it matters to your business, we want to hear about it.
                </p>
              </div>
              <Link to="/get-started"
                className="btn-glow group inline-flex shrink-0 items-center gap-2 rounded-xl px-6 py-3 text-sm font-bold transition-all"
                style={{ background: GRAD, color: '#ffffff', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Tell us your problem <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── How We Work ── */}
      <section className="py-20" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-5xl px-6">
          <motion.div>
            <motion.div className="mb-12 text-center">
              <span className="mb-3 inline-block text-xs font-semibold uppercase tracking-widest" style={{ color: GRAD, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Our Process</span>
              <h2 className="mb-4 text-3xl font-bold md:text-4xl" style={{ fontFamily: 'Outfit, sans-serif', color: fg, lineHeight: 1.08 }}>
                No surprises.{' '}
                <span style={{ color: fg }}>
                  No jargon.
                </span>
                {' '}Just results.
              </h2>
              <p className="text-base leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
                Here's exactly how we work — from the first conversation to long-term support.
              </p>
            </motion.div>
            <div className="flex flex-col gap-6">
              {STEPS.map(({ number, Icon, color, title, duration, description, outcomes }, i) => (
                <motion.div key={number} className="grid grid-cols-1 gap-6 rounded-2xl p-8 md:grid-cols-[auto_1fr] md:gap-10" style={{ background: surface, border: `1px solid ${border}` }}>
                  <div className="flex flex-row items-center gap-4 md:flex-col md:items-start md:gap-3">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl" style={{ background: `color-mix(in srgb, ${color} 12%, transparent)` }}>
                      <Icon size={24} style={{ color }} />
                    </div>
                    <span className="text-5xl font-bold leading-none" style={{ fontFamily: 'Outfit, sans-serif', color: `color-mix(in srgb, ${color} 20%, transparent)` }}>{number}</span>
                  </div>
                  <div>
                    <div className="mb-1 flex flex-wrap items-center gap-3">
                      <h3 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>{title}</h3>
                      <span className="rounded-full px-3 py-0.5 text-xs font-semibold" style={{ background: `color-mix(in srgb, ${color} 12%, transparent)`, color }}>{duration}</span>
                    </div>
                    <p className="mb-5 text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>{description}</p>
                    <ul className="flex flex-col gap-2">
                      {outcomes.map(o => (
                        <li key={o} className="flex items-start gap-2.5 text-sm" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
                          <CheckCircle2 size={15} className="mt-0.5 shrink-0" style={{ color }} />
                          {o}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Principles ── */}
      <section className="py-16" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-5xl px-6">
          <motion.div>
            <motion.div className="mb-10 text-center">
              <span className="mb-3 inline-block text-xs font-semibold uppercase tracking-widest" style={{ color: GRAD, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>How we operate</span>
              <h2 className="text-2xl font-bold md:text-3xl" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>The principles we never compromise on</h2>
            </motion.div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {PRINCIPLES.map(({ Icon, color, title, body }) => (
                <motion.div key={title} className="rounded-2xl p-6" style={{ background: surface, border: `1px solid ${border}` }}>
                  <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl" style={{ background: `color-mix(in srgb, ${color} 12%, transparent)` }}>
                    <Icon size={20} style={{ color }} />
                  </div>
                  <h3 className="mb-2 font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>{title}</h3>
                  <p className="text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>{body}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section id="faq-section" className="py-16" style={{ background: 'hsl(var(--background))' }}>
        <div className="mx-auto max-w-4xl px-6">
          <motion.div>
            <motion.div className="mb-10 text-center">
              <h2 className="text-2xl font-bold md:text-3xl" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>Common questions</h2>

              <p className="mt-3 text-sm" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
                Still need help? <Link to="/contact" style={{ color: GRAD }} className="font-semibold hover:underline">Get in touch directly →</Link>
              </p>
            </motion.div>
            <FaqCategories />
          </motion.div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section
        className="relative overflow-hidden px-6 pb-24 pt-4"
        style={{
          background: `linear-gradient(to bottom, hsl(var(--background)) 0%, hsl(var(--background)) 65%, ${surface} 100%)`,
        }}
      >
        <HeroWave fadeBottom={58} bleed="bottom" edgeFadeTo={surface} />
        <div className="relative mx-auto max-w-4xl">
          <motion.div className="flex flex-col items-center gap-10 pt-16 text-center">

            {/* Stats row */}
            <div className="flex flex-wrap justify-center gap-12">
              {[
                { stat: '10+',   label: 'Services under one roof' },
                { stat: 'Weeks', label: 'Not months, to go live' },
                { stat: '100%',  label: 'You own everything we build' },
              ].map(({ stat, label }) => (
                <div key={label} className="flex flex-col items-center gap-1">
                  <span className="text-4xl font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: fg }}>{stat}</span>
                  <span className="text-xs font-semibold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>{label}</span>
                </div>
              ))}
            </div>

            {/* Heading + copy */}
            <div className="max-w-xl">
              <h2 className="mb-3 text-2xl font-bold md:text-3xl" style={{ fontFamily: 'Outfit, sans-serif', color: fg, lineHeight: 1.1 }}>
                Not sure which service you need?
              </h2>
              <p className="text-sm leading-relaxed" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', color: fgMuted }}>
                That's completely normal. Most of our clients come to us knowing something needs to change but not exactly what. Get started and we'll figure it out together.
              </p>
            </div>

            {/* CTA */}
            <a href="https://wa.me/447511683542?text=Hi%20there,%20I'd%20like%20to%20enquire%20about%20your%20services" target="_blank" rel="noopener noreferrer"
              className="btn-glow group inline-flex items-center gap-2 rounded-full px-8 py-3.5 text-sm font-bold transition-all"
              style={{ background: `linear-gradient(135deg, ${GRAD} 0%, hsl(243 75% 45%) 100%)`, color: '#ffffff', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Get Started <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
            </a>

          </motion.div>
        </div>
      </section>
    </>
  );
}
